import _ from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Button, Chip, Grid, Typography } from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import { blueGrey } from "@mui/material/colors";
import { IconLink, IconMoodSpark } from "@tabler/icons-react";

import { setTabTitle } from "utils/misc";
import { APP_IDENTITY, WEB_CONFIG } from "config/app";
import UserCardUtils from "features/user/utils";
import Logger from "utils/logger";
import Timeline from "features/charts/Timeline";
import TopLevelMetrics from "features/history/TopLevelMetrics";
import MethodsOverTime from "features/charts/MethodsOverTime";
import AlbumComparison from "features/charts/AlbumComparison";
import MethodBreakdown from "features/charts/MethodBreakdown";
import RecentActivity from "features/progress/RecentActivity";
import MonthlyActivityChart from "features/charts/MonthlyActivity";
import CurrentSession from "data/userSession";
import User from "data/firestore/user";
import useCustomNavigation from "hooks/customNavigation";
import useCustomSnackbar from "hooks/customSnackbar";
import CopyUserId from "components/CopyUserId";

// Lazy load JS components to avoid TypeScript strict type checking
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomChip: any = require("components/Chip").default;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const UserCard: any = require("features/user/UserCard").default;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ExpandableCard: any = require("components/ExpandableCard").default;

interface TimelineDataPoint {
  date: string;
  value: number;
}

interface ProgressPageProps {
  // When rendered inside the profile overlay, the target user comes from here
  // instead of the URL, and the URL is not self-synced (the list URL stays put).
  overlayEntry?: { uid?: string | null; username?: string | null };
}

const ProgressPage = ({ overlayEntry }: ProgressPageProps = {}) => {
  const { enqueueSnackbar } = useCustomSnackbar();
  const navigate = useCustomNavigation();

  const [url] = useSearchParams();
  const [ID, setID] = useState<string | null>(null);
  const [days, setDays] = useState(0);
  const [duration, setDuration] = useState(0);
  const [sessions, setSessions] = useState(0);
  const [failed, setFailed] = useState<string | null>(null);
  const [timelineData, setTimelineData] = useState<TimelineDataPoint[]>([]);
  const [mainMetric, setMainMetric] = useState("Hours");

  const [viewedUserAbove18, setViewedUserAbove18] = useState(false);
  const [currentUserAbove18, setCurrentUserAbove18] = useState(false);
  const [viewedUser, setViewedUser] = useState<User | null>(null);
  const [isIncognito, setIsIncognito] = useState(false);

  // Update mainMetric when Timeline component changes it
  const handleMainMetricChange = (metric: string) => {
    setMainMetric(metric);
  };

  const failure = (message: string) => {
    enqueueSnackbar(message, { variant: "error" });
    setFailed(message);
  };

  const handleCopyProfileLink = async () => {
    if (!viewedUser) return;
    const profileUrl = UserCardUtils.buildProfileLink(
      WEB_CONFIG.homepage,
      viewedUser.profileLink
    );
    try {
      await navigator.clipboard.writeText(profileUrl);
      enqueueSnackbar("Profile link copied to clipboard!", {
        variant: "success",
      });
    } catch (error) {
      Logger.error("Failed to copy profile link:", error);
      enqueueSnackbar(`Your profile link is: ${profileUrl}`, {
        variant: "info",
      });
    }
  };

  const loadUser = async (isCancelled: () => boolean = () => false) => {
    const username = overlayEntry
      ? overlayEntry.username ?? null
      : url.get("user");
    let uid: string | null = overlayEntry
      ? overlayEntry.uid ?? null
      : url.get("uid");

    // If no parameters provided, default to current user or demo user
    if (!username && !uid) {
      // Wait for session to be loaded before checking authentication
      await CurrentSession.load();
      if (CurrentSession.isSignedIn) {
        uid = CurrentSession.user.uid ?? null;
      } else {
        uid = APP_IDENTITY.demoUserId;
      }
    }

    // Wait for session to be loaded before checking authentication
    await CurrentSession.load();
    // Bail if the user navigated away while we were loading -- otherwise a late
    // setID() would trigger the [ID] effect and navigate them back to /progress.
    if (isCancelled()) return;

    // Check current user's above18 status
    setCurrentUserAbove18(CurrentSession.user?.above18 || false);

    // Check if current user is incognito and viewing someone else's page
    const isViewingOther =
      (username &&
        username.toLowerCase() !==
          (CurrentSession.user?.name as unknown as string)?.toLowerCase()) ||
      (uid && uid !== CurrentSession.user?.uid);
    if (CurrentSession.user?.incognito && isViewingOther) {
      setIsIncognito(true);
      return;
    }

    // First check if authenticated
    if (
      !CurrentSession.isSignedIn &&
      uid !== APP_IDENTITY.demoUserId &&
      username?.toLowerCase() !== APP_IDENTITY.demoUsername.toLowerCase()
    ) {
      failure("You must be logged in to see other users' data");
      return;
    }

    // Query by username
    if (username) {
      try {
        const userID = await User.UsernameToUID(username);
        if (isCancelled()) return;
        setID(userID);
      } catch {
        failure(`User (${username}) not found`);
      }
    }
    // If uid is provided, validate it exists
    else if (uid) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const user = new User(uid as any);
        const response = await user.fetch();
        if (isCancelled()) return;
        setID(uid);
        if (!response.data) {
          failure(`User (${uid}) not found`);
        }
      } catch {
        failure(`User (${uid}) not found`);
      }
    }
  };

  useEffect(() => {
    setTabTitle("Progress");
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadUser(() => cancelled);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, overlayEntry?.uid, overlayEntry?.username]);

  useEffect(() => {
    if (!ID) {
      return;
    }
    // Guard against the load resolving after the user has navigated away: a bare
    // navigate() in a .then still fires on an unmounted component and would yank
    // the user back to /progress. The cancelled flag makes it a no-op.
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const user = new User(ID as any);
    user.load(true).then(() => {
      if (cancelled) return;
      // If viewed user is incognito and it's not the current user, treat as not found
      if (user.incognito && user.uid !== CurrentSession.user?.uid) {
        setFailed(`User not found`);
        return;
      }
      setViewedUser(user);
      setDays(user.snapshot.total.days);
      setDuration(user.snapshot.total.duration);
      setSessions(user.snapshot.total.sessions);

      // Check viewed user's above18 status
      setViewedUserAbove18(user.above18 || false);

      // In overlay mode the list URL stays put; only sync the URL for the
      // standalone /progress route (deep links / shared profile URLs).
      if (!overlayEntry) {
        if (user.name) {
          navigate("progress", `?user=${user.name}`, true);
        } else {
          navigate("progress", `?uid=${user.uid}`, true);
        }
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ID]);

  if (isIncognito) {
    return (
      <Grid container justifyContent="center" sx={{ p: 2 }}>
        <Chip
          icon={<ErrorOutline />}
          color="error"
          label="Incognito mode disables this feature"
        />
      </Grid>
    );
  }

  if (failed || !ID) {
    return <Typography variant="overline">{failed}</Typography>;
  }

  return (
    <Grid container flexDirection="column" justifyContent="center">
      <Grid item sx={{ mb: 2 }}>
        <UserCard uid={ID} user={viewedUser} variant="profile" />
      </Grid>
      <Grid container>
        <TopLevelMetrics
          days={days}
          duration={duration}
          sessions={sessions}
          previousMonthData={undefined}
        />
      </Grid>
      <Grid item style={{ marginTop: 20, width: "100%" }}>
        <Timeline
          uid={ID}
          onTimelineDataChange={setTimelineData}
          mainMetric={mainMetric}
          onMainMetricChange={handleMainMetricChange}
        />
      </Grid>
      <Grid item style={{ marginTop: 20, width: "100%" }}>
        <MethodsOverTime
          uid={ID}
          timelineData={timelineData}
          metric={_.lowerCase(mainMetric)}
        />
      </Grid>
      <Grid item style={{ marginTop: 20, width: "100%" }}>
        <MethodBreakdown uid={ID} />
      </Grid>
      <Grid item style={{ marginTop: 20, width: "100%" }}>
        <ExpandableCard title="This Month So Far">
          <MonthlyActivityChart
            uid={ID}
            month={moment().format("YYYY-MM-DD")}
          />
        </ExpandableCard>
      </Grid>
      <Grid item style={{ marginTop: 20, width: "100%" }}>
        <RecentActivity uid={ID} />
      </Grid>
      {viewedUserAbove18 && currentUserAbove18 && (
        <Grid item style={{ marginTop: 20, width: "100%" }}>
          <AlbumComparison uid={ID} />
        </Grid>
      )}
      <Grid item style={{ marginTop: 20, width: "100%", textAlign: "center" }}>
        <CustomChip
          icon={<IconMoodSpark />}
          label="Any graph suggestions? Shoot me a message!"
          color="info"
        />
      </Grid>
      <Grid item style={{ marginTop: 20, width: "100%", textAlign: "center" }}>
        <Button
          variant="text"
          startIcon={<IconLink size={18} />}
          onClick={handleCopyProfileLink}
          sx={{ color: blueGrey[600] }}
        >
          Copy Profile Link
        </Button>
      </Grid>
      <Grid
        item
        style={{
          marginTop: 20,
          width: "100%",
          textAlign: "center",
          color: blueGrey[500],
        }}
      >
        Joined:{" "}
        {viewedUser?.dates?.joined
          ? moment(viewedUser.dates.joined).format("MMMM D, YYYY")
          : ""}
      </Grid>
      <Grid
        item
        style={{
          marginTop: 20,
          width: "100%",
          textAlign: "center",
          color: blueGrey[500],
        }}
      >
        <CopyUserId id={ID} />
      </Grid>
    </Grid>
  );
};

ProgressPage.displayName = "ProgressPage";

export default ProgressPage;
